import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Grid,
  TextField,
  MenuItem,
  Fab,
  Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { LclDataContext } from "../../Contexts/LclDataContext";
import ButtonCreate from "../ButtonCreate";
import UploadFileSection from "../UploadFileSection";
import ButtonUpload from "../ButtonUpload";
import { UserDataContext } from "../../Contexts/UserDataContext";
import { roles, invoiceStatuses } from "../../Mappings";

const CreateDpc = (props) => {
  const [files, setFiles] = useState([]);
  const { truckId, week, day, ...otherProps } = props;
  const { createLclModel, comments, defaultLcl } = useContext(LclDataContext);
  const lclModel = createLclModel({ week });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { userData } = useContext(UserDataContext);

  const disabled =
    !(
      userData.invoiceStatuses[props.truckId][props.week] ===
        invoiceStatuses.unsubmitted ||
      userData.invoiceStatuses[props.truckId][props.week] ===
        invoiceStatuses.dpcPending
    ) || userData.roleId === roles.tollManager;

  const displayColumns = [
    "eventTimeString",
    "conNoteNum",
    "eventType",
    "branch",
    "sender",
    "location",
    "zone",
    "receiver",
    "eventAddress",
    "eventItems",
    "kg",
    "m3",
    "stopPay",
    "homeDelivery",
    "dpc",
    "reason",
    comments,
  ];

  const reasons = [
    "Missing StopPay",
    "Duplicate StopPay",
    "Incorrect Suburb",
    "Load rate",
    "Incorrect DPC rate",
    "Incorrect number of Items",
    "Incorrect M3",
    "Incorrect Weight",
    "Other",
  ];

  const [formData, setFormData] = useState({
    values: {
      ...displayColumns.reduce((acc, cur) => ({ ...acc, [cur]: "" }), {}),
      week,
      day,
    },
    valid: {},
  });

  useEffect(() => {
    setFormData({
      ...formData,
      valid: {
        ...displayColumns.reduce(
          (acc, cur) => ({
            ...acc,
            [cur]: lclModel[cur].validate(formData.values),
          }),
          {}
        ),
      },
    });
  }, [formData.values]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      values: { ...formData.values, [name]: value },
    });
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      values: {
        ...displayColumns.reduce((acc, cur) => ({ ...acc, [cur]: "" }), {}),
        week,
        day,
      },
      valid: {},
    });
    setFiles([]);
  };

  return (
    <>
      <Tooltip title="Create">
        <div>
          <Fab
            disabled={disabled}
            onClick={() => {
              setOpen(true);
            }}
            color="primary"
          >
            <AddIcon />
          </Fab>
        </div>
      </Tooltip>

      <Dialog
        {...otherProps}
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create New Item</DialogTitle>
        <Divider />
        <DialogContent>
          <DialogContentText component="span">
            <Grid
              container
              direction="row"
              justify="flex-start"
              alignItems="center"
              spacing={1}
            >
              {displayColumns.map((col) => (
                <React.Fragment key={col}>
                  <Grid item xs={3}>
                    {lclModel[col].displayName}:
                  </Grid>
                  <Grid item xs={9}>
                    <TextField
                      fullWidth
                      value={formData.values[col]}
                      onChange={(event) => handleChange(event)}
                      error={!formData.valid[col]}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      {...lclModel[col].textFieldProps}
                      {...(col === "reason" && {
                        children: reasons.map((e) => (
                          <MenuItem key={e} value={e}>
                            {e}
                          </MenuItem>
                        )),
                      })}
                    />
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </DialogContentText>
        </DialogContent>
        <Divider />
        <UploadFileSection files={files} setFiles={setFiles} />
        <Divider />
        <DialogActions>
          <ButtonUpload
            disabled={loading}
            color="primary"
            variant="contained"
            maxFileSize={10000000}
            accept={[".png", ".jpg", ".pdf", ".docx", ".xlsx", ".csv"]}
            multiple={false}
            files={files}
            setFiles={setFiles}
          >
            Browse
          </ButtonUpload>
          <ButtonCreate
            disabled={
              Object.values(formData.valid).includes(false) ||
              files.length === 0
            }
            variant="contained"
            color="primary"
            controller="lcls"
            truckId={truckId}
            week={week}
            day={day}
            itemData={{
              ...defaultLcl,
              ...displayColumns.reduce((acc, col) => {
                acc[col] = lclModel[col].mapToRequestValue(formData.values);
                return acc;
              }, {}),
              eventDateTime: lclModel.eventDateTime.mapToRequestValue(
                formData.values
              ),
              driver: Number(truckId),
            }}
            files={files}
            callback={handleClose}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateDpc;

CreateDpc.propTypes = {
  truckId: PropTypes.number.isRequired,
  week: PropTypes.number.isRequired,
  day: PropTypes.number.isRequired,
};
